/**
 * Understudy's operator console: the simulated Revizto project, the changes an
 * operator can make to it, the demo scenario, and the traffic journal that
 * shows Bear polling and picking those changes up.
 *
 * It talks to the simulator at `UNDERSTUDY_URL` (its Worker, or the dev
 * server's proxy) and stays live over its event stream.
 */
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CircleCheck,
  FileJson,
  Mail,
  HeartPulse,
  MessageSquarePlus,
  Play,
  Plus,
  RotateCcw,
  UserRound,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { ExchangeEntry, JournalEntry } from "understudy/framework/journal";
import type { ScenarioRun } from "understudy/framework/scenario";
import type { ReviztoWorld } from "understudy/revizto/world";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UNDERSTUDY_URL } from "@/lib/understudy";
import { cn } from "@/lib/utils";

import "./-agent-first.css";
import {
  DeliveryRow,
  describeEmailRequest,
  describeEmailResponse,
  EmailPanel,
  type EmailWorldState,
} from "./-understudy-email";

export const Route = createFileRoute("/prototypes/understudy")({
  component: Understudy,
});

const BASE = UNDERSTUDY_URL;
const STATUSES = ["Open", "In progress", "Solved", "Closed"] as const;
const PRIORITIES = ["None", "Minor", "Major", "Critical"] as const;

type World = ReturnType<ReviztoWorld["snapshot"]>;
type Person = World["people"][number];
interface ScenarioInfo {
  id: string;
  title: string;
  description: string;
  steps: string[];
}
interface PackState<W = World> {
  title: string;
  world: W;
  scenarios: ScenarioInfo[];
}
interface SimState {
  packs: { revizto: PackState; email: PackState<EmailWorldState> };
  scenario: ScenarioRun;
}
type Tab = keyof SimState["packs"];

/** What each service is called when it answers in the journal. */
const SERVICE: Record<string, string> = {
  revizto: "Revizto",
  email: "Email Service",
};

/** The simulator's state and journal, kept live over its event stream. */
function useSimulator() {
  const [state, setState] = useState<SimState | null>(null);
  const [journal, setJournal] = useState<JournalEntry[]>([]);
  const [online, setOnline] = useState<boolean | null>(null);
  useEffect(() => {
    const events = new EventSource(`${BASE}/control/events`);
    events.addEventListener("hello", (e) => {
      const hello: SimState & { journal: JournalEntry[] } = JSON.parse(e.data);
      setState(hello);
      setJournal(hello.journal);
      setOnline(true);
    });
    events.addEventListener("state", (e) => setState(JSON.parse(e.data)));
    events.addEventListener("entry", (e) => {
      const entry: JournalEntry = JSON.parse(e.data);
      setJournal((j) => [...j, entry]);
    });
    events.addEventListener("reset", () => setJournal([]));
    events.onerror = () => setOnline(false);
    return () => events.close();
  }, []);
  return { state, journal, online };
}

async function control(path: string, body?: unknown) {
  const response = await fetch(`${BASE}/control${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error((await response.json()).error);
}

function Understudy() {
  const { state, journal, online } = useSimulator();
  const [selected, setSelected] = useState(102);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(
    window.location.hash === "#email" ? "email" : "revizto",
  );
  const choose = (next: Tab) => {
    setTab(next);
    window.history.replaceState(null, "", `#${next}`);
  };
  const pack = state?.packs[tab];

  return (
    <div className="af-theme flex h-dvh flex-col text-[14px]">
      <SimulatorBanner tab={tab} />
      {online === false && !state ? (
        <Offline />
      ) : !pack || !state ? (
        <div className="grid flex-1 place-items-center text-muted-foreground">
          Connecting to the simulator…
        </div>
      ) : (
        <>
          <Header
            tab={tab}
            onTab={choose}
            state={state}
            run={state.scenario}
            journal={journal}
          />
          <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(420px,0.9fr)] gap-0 border-t">
            <section className="flex min-h-0 flex-col overflow-hidden">
              {tab === "revizto" ? (
                <>
                  <IssueList
                    world={state.packs.revizto.world}
                    selected={selected}
                    onSelect={setSelected}
                    journal={journal}
                  />
                  <IssueDetail
                    world={state.packs.revizto.world}
                    number={selected}
                  />
                </>
              ) : (
                <EmailPanel world={state.packs.email.world} />
              )}
            </section>
            <Journal
              entries={journal}
              highlight={highlight}
              onHighlight={setHighlight}
            />
          </div>
        </>
      )}
    </div>
  );
}

function SimulatorBanner({ tab }: { tab: Tab }) {
  return (
    <div className="flex items-center gap-3 bg-[repeating-linear-gradient(-45deg,#f5b301_0_14px,#e8a400_14px_28px)] px-5 py-2 text-[13px] font-semibold text-[#1c1400]">
      <span className="rounded bg-[#1c1400] px-2 py-0.5 text-[11px] tracking-[0.12em] text-[#f5b301]">
        SIMULATOR
      </span>
      <span>
        Understudy is playing Revizto and Cloudflare's email services. Fake
        project, fake people, fake tokens. It never contacts a real service and
        holds no real data.
      </span>
      <a
        className="ml-auto flex items-center gap-1 underline-offset-2 hover:underline"
        href={`${BASE}/openapi/${tab}.json`}
        target="_blank"
        rel="noreferrer"
      >
        <FileJson className="size-4" /> Simulated {SERVICE[tab]} API (OpenAPI)
      </a>
    </div>
  );
}

function Offline() {
  return (
    <div className="grid flex-1 place-items-center">
      <div className="max-w-md text-center">
        <h1 className="mb-2 text-xl font-semibold">
          The simulator isn't running
        </h1>
        <p className="text-muted-foreground">
          Understudy didn't answer at <code>{BASE}</code>. In dev it runs with{" "}
          <code>bun run dev</code>; deployed, it's the <code>understudy</code>{" "}
          Worker.
        </p>
      </div>
    </div>
  );
}

function Header({
  tab,
  onTab,
  state,
  run,
  journal,
}: {
  tab: Tab;
  onTab: (tab: Tab) => void;
  state: SimState;
  run: ScenarioRun;
  journal: JournalEntry[];
}) {
  const scenario = state.packs[tab].scenarios[0];
  const { lastPoll, cadenceMs } = usePollCadence(journal);
  const now = useNow();
  const since = lastPoll ? now - Date.parse(lastPoll.at) : null;
  return (
    <header className="flex flex-wrap items-center gap-x-8 gap-y-3 px-6 py-4">
      <div className="min-w-0">
        <div className="flex items-center gap-1 text-[12px] font-medium tracking-wide text-muted-foreground uppercase">
          Understudy <span className="text-border">/</span>
          {(["revizto", "email"] as const).map((t) => (
            <button
              key={t}
              onClick={() => onTab(t)}
              className={cn(
                "flex items-center gap-1 rounded px-1.5 py-0.5 uppercase",
                t === tab
                  ? "bg-[var(--af-selected)] text-foreground"
                  : "hover:bg-[var(--af-soft)]",
              )}
            >
              {t === "email" && <Mail className="size-3.5" />}
              {state.packs[t].title}
            </button>
          ))}
        </div>
        <h1 className="font-['Agent_Satoshi'] text-[26px] leading-tight font-bold tracking-tight">
          {tab === "revizto"
            ? state.packs.revizto.world.project.name
            : "Email to a Bear project"}
        </h1>
        {tab === "email" ? (
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            Delivered to Bear's inbox the way Email Routing delivers it; Bear's
            replies land here.
          </div>
        ) : (
          <div className="mt-0.5 flex items-center gap-2 text-[13px] text-muted-foreground">
            <span
              className={cn(
                "inline-block size-2 rounded-full",
                since !== null && since < 15_000
                  ? "bg-[#2fb36d]"
                  : "bg-[#b8c2cc]",
                since !== null && since < 1200 && "animate-ping",
              )}
            />
            {since === null ? (
              "Waiting for Bear: in a Bear project, add the Revizto Simulator jig and open it"
            ) : (
              <span className="tabular-nums">
                {cadenceMs !== null && (
                  <b className="font-semibold text-foreground">
                    Bear polls every {Math.round(cadenceMs / 1000)}s
                  </b>
                )}
                {cadenceMs !== null && " · "}
                last poll {ago(since)}
                {cadenceMs !== null &&
                  since < cadenceMs * 3 &&
                  ` · next in ~${Math.max(0, Math.round((cadenceMs - since) / 1000))}s`}
              </span>
            )}
          </div>
        )}
      </div>
      {scenario && <ScenarioCard pack={tab} scenario={scenario} run={run} />}
    </header>
  );
}

/**
 * Bear's polls (one issue read each) and their usual spacing: the median gap
 * between the last few, so a manual "Sync now" doesn't skew it.
 */
function usePollCadence(journal: JournalEntry[]) {
  return useMemo(() => {
    const polls = journal.filter(
      (e): e is ExchangeEntry =>
        e.kind === "exchange" && e.operation === "issueFilter",
    );
    const times = polls.slice(-6).map((p) => Date.parse(p.at));
    const gaps = times
      .slice(1)
      .map((t, i) => t - (times[i] ?? t))
      .sort((a, b) => a - b);
    return {
      lastPoll: polls.at(-1) ?? null,
      cadenceMs:
        gaps.length > 0 ? (gaps[Math.floor(gaps.length / 2)] ?? null) : null,
    };
  }, [journal]);
}

function ScenarioCard({
  pack,
  scenario,
  run,
}: {
  pack: Tab;
  scenario: ScenarioInfo;
  run: ScenarioRun;
}) {
  const active =
    run.state !== "idle" && run.scenario === scenario.id ? run : null;
  return (
    <div className="ml-auto flex items-center gap-4 rounded-lg border bg-card px-4 py-3 shadow-xs">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold">{scenario.title}</div>
        <ol className="mt-1 flex gap-1.5">
          {scenario.steps.map((step, i) => (
            <li
              key={step}
              title={step}
              className={cn(
                "rounded px-2 py-0.5 text-[11px]",
                active && i < active.step
                  ? "bg-[var(--af-success-tint)] text-[var(--af-success)]"
                  : active?.state === "running" && i === active.step
                    ? "animate-pulse bg-[var(--af-amber-tint)] text-[var(--af-amber)]"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {i + 1}. {step.replace(/ \(.*\)$/, "")}
            </li>
          ))}
        </ol>
      </div>
      <Button onClick={() => void control(`/scenarios/${pack}/${scenario.id}`)}>
        <Play /> {active?.state === "running" ? "Restart" : "Run scenario"}
      </Button>
      <Button
        variant="ghost"
        onClick={() => void control("/reset")}
        title="Reset the project and clear the journal"
      >
        <RotateCcw /> Reset
      </Button>
    </div>
  );
}

const STATUS_STYLE: Record<string, string> = {
  Open: "bg-[#e6eef6] text-[#3f6e93]",
  "In progress": "bg-[var(--af-amber-tint)] text-[var(--af-amber)]",
  Solved: "bg-[var(--af-success-tint)] text-[var(--af-success)]",
  Closed: "bg-muted text-muted-foreground",
};

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "rounded-full px-2.5 py-0.5 text-[12px] font-medium whitespace-nowrap",
        STATUS_STYLE[status],
      )}
    >
      {status}
    </span>
  );
}

function IssueList({
  world,
  selected,
  onSelect,
  journal,
}: {
  world: World;
  selected: number;
  onSelect: (n: number) => void;
  journal: JournalEntry[];
}) {
  const [creating, setCreating] = useState(false);
  const pending = usePendingIssues(journal);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-6 pt-4 pb-2">
        <h2 className="text-[15px] font-semibold">
          Issues{" "}
          <span className="font-normal text-muted-foreground">
            {world.issues.length}
          </span>
        </h2>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus /> New issue
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-3">
        <table className="w-full border-separate border-spacing-y-0.5">
          <tbody>
            {world.issues.map((issue) => (
              <tr
                key={issue.uuid}
                onClick={() => onSelect(issue.id)}
                className={cn(
                  "cursor-pointer [&>td]:py-2.5 [&>td:first-child]:rounded-l-md [&>td:first-child]:pl-3 [&>td:last-child]:rounded-r-md [&>td:last-child]:pr-3",
                  issue.id === selected
                    ? "bg-[var(--af-selected)]"
                    : "hover:bg-[var(--af-soft)]",
                )}
              >
                <td className="w-14 font-medium text-muted-foreground tabular-nums">
                  #{issue.id}
                </td>
                <td className="w-12">
                  <span className="rounded border px-1.5 text-[11px] text-muted-foreground">
                    {issue.stampAbbr}
                  </span>
                </td>
                <td className="pr-3">
                  <div className="font-medium">{issue.title}</div>
                  <div className="text-[12px] text-muted-foreground">
                    {issue.priority} ·{" "}
                    {personOf(world, issue.assignee)?.name ?? "Unassigned"}
                    {issue.commentsCount > 0 &&
                      ` · ${issue.commentsCount} comment${issue.commentsCount === 1 ? "" : "s"}`}
                  </div>
                </td>
                <td className="w-40 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {pending.has(issue.uuid) && (
                      <span
                        className="text-[11px] text-[var(--af-amber)]"
                        title="Changed since Bear last read it"
                      >
                        waiting for Bear
                      </span>
                    )}
                    <StatusPill status={issue.status} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewIssueDialog
        world={world}
        open={creating}
        onOpenChange={setCreating}
        onCreated={onSelect}
      />
    </div>
  );
}

/** Issues with a change Bear's polls haven't delivered yet. */
function usePendingIssues(journal: JournalEntry[]) {
  return useMemo(() => {
    const delivered = new Set(
      journal.flatMap((e) => (e.kind === "exchange" ? e.causes : [])),
    );
    const pending = new Set<string>();
    for (const e of journal) {
      if (e.kind !== "change" || delivered.has(e.id)) continue;
      for (const key of e.keys)
        if (key.startsWith("issue:")) pending.add(key.slice("issue:".length));
    }
    return pending;
  }, [journal]);
}

function IssueDetail({ world, number }: { world: World; number: number }) {
  const issue = world.issues.find((i) => i.id === number);
  const [by, setBy] = useState<string>(world.people[0].email);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!issue) return null;
  const act = (promise: Promise<void>) => {
    setError(null);
    promise.catch((e: Error) => setError(e.message));
  };
  return (
    <div className="border-t bg-[var(--af-shell)] px-6 py-4">
      <div className="mb-3 flex items-baseline gap-3">
        <span className="text-muted-foreground tabular-nums">#{issue.id}</span>
        <h3 className="min-w-0 flex-1 truncate text-[16px] font-semibold">
          {issue.title}
        </h3>
        <PersonPicker people={world.people} value={by} onChange={setBy} />
      </div>
      <div className="mb-3 flex items-center gap-3">
        <span className="w-20 text-[12px] text-muted-foreground">Status</span>
        <div className="flex gap-1 rounded-lg border bg-card p-1">
          {STATUSES.map((status) => (
            <button
              key={status}
              disabled={status === issue.status}
              onClick={() =>
                act(
                  control(`/revizto/issues/${issue.uuid}/status`, {
                    status,
                    by,
                  }),
                )
              }
              className={cn(
                "rounded-md px-3 py-1 text-[13px]",
                status === issue.status
                  ? "bg-[var(--af-selected)] font-semibold"
                  : "text-muted-foreground hover:bg-[var(--af-soft)]",
              )}
            >
              {status}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-start gap-3">
        <span className="w-20 pt-2 text-[12px] text-muted-foreground">
          Comment
        </span>
        <div className="flex-1">
          <ol className="mb-2 max-h-28 space-y-1 overflow-auto text-[13px]">
            {issue.comments.map((c) => (
              <li key={c.uuid}>
                <span className="font-medium">
                  {personOf(world, c.author)?.name}
                </span>{" "}
                <span className="text-muted-foreground">{c.text}</span>
              </li>
            ))}
          </ol>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!text.trim()) return;
              act(
                control(`/revizto/issues/${issue.uuid}/comments`, {
                  author: by,
                  text,
                }).then(() => setText("")),
              );
            }}
          >
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Comment as ${personOf(world, by)?.name}`}
              className="min-h-9 bg-card"
              rows={1}
            />
            <Button type="submit" variant="outline">
              <MessageSquarePlus /> Add
            </Button>
          </form>
        </div>
      </div>
      {error && <p className="mt-2 text-[13px] text-destructive">{error}</p>}
    </div>
  );
}

function PersonPicker({
  people,
  value,
  onChange,
}: {
  people: readonly Person[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-auto gap-2 bg-card text-[13px]">
        <UserRound className="size-4 text-muted-foreground" />
        <span className="text-muted-foreground">As</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {people.map((p) => (
          <SelectItem key={p.email} value={p.email}>
            {p.name} <span className="text-muted-foreground">· {p.role}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NewIssueDialog({
  world,
  open,
  onOpenChange,
  onCreated,
}: {
  world: World;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (n: number) => void;
}) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<string>("Major");
  const [reporter, setReporter] = useState<string>(world.people[0].email);
  const [assignee, setAssignee] = useState<string>(world.people[1].email);
  const nextNumber = Math.max(...world.issues.map((i) => i.id)) + 1;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="af-theme">
        <DialogHeader>
          <DialogTitle>New issue #{nextNumber}</DialogTitle>
          <DialogDescription>
            Opened in the simulated Revizto project. Bear sees it on its next
            poll.
          </DialogDescription>
        </DialogHeader>
        <form
          id="new-issue"
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void control("/revizto/issues", {
              title,
              priority,
              stampAbbr: "CL",
              reporter,
              assignee,
            }).then(() => {
              onCreated(nextNumber);
              setTitle("");
              onOpenChange(false);
            });
          }}
        >
          <Input
            autoFocus
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Conduit through shear wall SW-3"
          />
          <div className="grid grid-cols-3 gap-2">
            <Field label="Priority">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Reporter">
              <PeoplePlain
                people={world.people}
                value={reporter}
                onChange={setReporter}
              />
            </Field>
            <Field label="Assignee">
              <PeoplePlain
                people={world.people}
                value={assignee}
                onChange={setAssignee}
              />
            </Field>
          </div>
        </form>
        <DialogFooter>
          <Button type="submit" form="new-issue">
            Open issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-[12px] text-muted-foreground">
      {label}
      {children}
    </label>
  );
}

function PeoplePlain({
  people,
  value,
  onChange,
}: {
  people: readonly Person[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {people.map((p) => (
          <SelectItem key={p.email} value={p.email}>
            {p.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type Row =
  | { kind: "change"; entry: Extract<JournalEntry, { kind: "change" }> }
  | { kind: "delivery"; entry: Extract<JournalEntry, { kind: "delivery" }> }
  | { kind: "exchange"; entry: ExchangeEntry }
  | { kind: "heartbeat"; entries: ExchangeEntry[] };

/**
 * Quiet polls in a row fold into one heartbeat row. A poll is one issue read;
 * the quiet reads that follow it in the same poll (no deletions, say) are part
 * of it, so they neither count as polls nor break up the heartbeat.
 */
function rowsOf(entries: JournalEntry[]): Row[] {
  const rows: Row[] = [];
  for (const entry of entries) {
    const last = rows.at(-1);
    if (entry.kind === "change") rows.push({ kind: "change", entry });
    else if (entry.kind === "delivery") rows.push({ kind: "delivery", entry });
    else if (!isQuiet(entry)) rows.push({ kind: "exchange", entry });
    else if (entry.operation !== "issueFilter") continue;
    else if (last?.kind === "heartbeat") last.entries.push(entry);
    else rows.push({ kind: "heartbeat", entries: [entry] });
  }
  return rows;
}

const isQuiet = (e: ExchangeEntry) =>
  e.causes.length === 0 && e.response.status === 200 && countOf(e) === 0;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** The items of a Revizto page response, or none. */
function itemsOf(e: ExchangeEntry): Record<string, unknown>[] {
  const body = e.response.body;
  const page = isRecord(body) && isRecord(body.data) ? body.data.data : null;
  return Array.isArray(page) ? page.filter(isRecord) : [];
}

const countOf = (e: ExchangeEntry) => itemsOf(e).length;

function Journal({
  entries,
  highlight,
  onHighlight,
}: {
  entries: JournalEntry[];
  highlight: string | null;
  onHighlight: (id: string | null) => void;
}) {
  const rows = useMemo(() => rowsOf(entries), [entries]);
  const deliveredBy = useMemo(() => {
    const map = new Map<string, ExchangeEntry>();
    for (const e of entries)
      if (e.kind === "exchange")
        for (const id of e.causes) if (!map.has(id)) map.set(id, e);
    return map;
  }, [entries]);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [rows.length]);

  return (
    <aside className="flex min-h-0 flex-col border-l bg-[var(--af-shell)]">
      <div className="flex items-baseline justify-between px-5 pt-4 pb-2">
        <h2 className="text-[15px] font-semibold">Traffic journal</h2>
        <span className="text-[12px] text-muted-foreground">
          Bear ↔ simulated services, live
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-auto px-4 pb-4">
        {rows.length === 0 && (
          <p className="px-1 py-8 text-center text-muted-foreground">
            Nothing yet. Make a change, run the scenario, or wait for Bear's
            next poll.
          </p>
        )}
        {rows.map((row) =>
          row.kind === "change" ? (
            <ChangeRow
              key={row.entry.id}
              change={row.entry}
              deliveredBy={deliveredBy.get(row.entry.id)}
              highlighted={highlight === row.entry.id}
              onHighlight={onHighlight}
            />
          ) : row.kind === "delivery" ? (
            <DeliveryRow
              key={row.entry.id}
              delivery={row.entry}
              highlighted={highlight === row.entry.cause}
            />
          ) : row.kind === "heartbeat" ? (
            <Heartbeat key={row.entries[0]?.id} entries={row.entries} />
          ) : (
            <ExchangeRow
              key={row.entry.id}
              exchange={row.entry}
              highlight={highlight}
              onHighlight={onHighlight}
            />
          ),
        )}
        <div ref={end} />
      </div>
    </aside>
  );
}

function ChangeRow({
  change,
  deliveredBy,
  highlighted,
  onHighlight,
}: {
  change: Extract<JournalEntry, { kind: "change" }>;
  deliveredBy: ExchangeEntry | undefined;
  highlighted: boolean;
  onHighlight: (id: string | null) => void;
}) {
  const lag =
    deliveredBy && (Date.parse(deliveredBy.at) - Date.parse(change.at)) / 1000;
  return (
    <div
      onMouseEnter={() => onHighlight(change.id)}
      onMouseLeave={() => onHighlight(null)}
      className={cn(
        "rounded-lg border border-dashed border-[#d9c7a3] bg-[var(--af-amber-tint)] px-3 py-2 transition-shadow",
        highlighted && "ring-2 ring-[#e8a400]",
      )}
    >
      <div className="flex items-center gap-2 text-[11px] font-medium tracking-wide text-[var(--af-amber)] uppercase">
        {change.actor === "scenario" ? "Scenario" : "Operator"} change ·{" "}
        {change.id}
        <span className="ml-auto font-normal tracking-normal normal-case">
          {clock(change.at)}
        </span>
      </div>
      <div className="mt-0.5 text-[14px]">{change.summary}</div>
      <div className="mt-1 text-[12px] text-muted-foreground">
        {change.keys.length === 0 ? null : deliveredBy ? (
          <span className="flex items-center gap-1 text-[var(--af-success)]">
            <CircleCheck className="size-3.5" /> Bear picked this up in{" "}
            {deliveredBy.id}, {lag?.toFixed(0)}s later
          </span>
        ) : (
          <span className="animate-pulse">
            {change.pack === "email"
              ? "Waiting for Bear's reply…"
              : "Waiting for Bear's next poll…"}
          </span>
        )}
      </div>
    </div>
  );
}

function Heartbeat({ entries }: { entries: ExchangeEntry[] }) {
  const last = entries.at(-1);
  return (
    <div className="flex items-center gap-2 px-1 py-0.5 text-[12px] text-muted-foreground">
      <HeartPulse className="size-4 text-[#c46a6a]" />
      {entries.length === 1
        ? "Bear polled, nothing new"
        : `${entries.length} polls, nothing new`}
      {last && <span className="ml-auto tabular-nums">{clock(last.at)}</span>}
    </div>
  );
}

function ExchangeRow({
  exchange,
  highlight,
  onHighlight,
}: {
  exchange: ExchangeEntry;
  highlight: string | null;
  onHighlight: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const linked = highlight !== null && exchange.causes.includes(highlight);
  const ok = exchange.response.status < 400;
  return (
    <div
      className={cn(
        "rounded-lg border bg-card shadow-xs transition-shadow",
        linked && "ring-2 ring-[#e8a400]",
      )}
    >
      <button
        className="w-full px-3 py-2 text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 text-[13px]">
          <ArrowUpRight className="size-4 text-[var(--af-blue)]" />
          <span className="font-semibold">Bear</span>
          <span className="truncate font-mono text-[12px] text-muted-foreground">
            {exchange.request.method} {describeRequest(exchange)}
          </span>
          <span className="ml-auto text-[12px] text-muted-foreground tabular-nums">
            {clock(exchange.at)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[13px]">
          <ArrowDownLeft
            className={cn(
              "size-4",
              ok ? "text-[var(--af-success)]" : "text-destructive",
            )}
          />
          <span className="font-semibold">
            {SERVICE[exchange.pack] ?? exchange.pack}
          </span>
          <span
            className={cn("font-mono text-[12px]", !ok && "text-destructive")}
          >
            {exchange.response.status}
          </span>
          <span className="text-muted-foreground">
            {describeResponse(exchange)}
          </span>
          <span className="ml-auto text-[12px] text-muted-foreground tabular-nums">
            {exchange.latencyMs} ms
          </span>
        </div>
        {exchange.causes.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {exchange.causes.map((id) => (
              <span
                key={id}
                onMouseEnter={() => onHighlight(id)}
                onMouseLeave={() => onHighlight(null)}
                className="rounded bg-[var(--af-amber-tint)] px-1.5 py-0.5 text-[11px] text-[var(--af-amber)]"
              >
                delivers {id}
              </span>
            ))}
          </div>
        )}
      </button>
      {open && (
        <div className="grid gap-2 border-t px-3 py-2 text-[12px]">
          <Json
            label="Request"
            value={{
              path: exchange.request.path,
              query: exchange.request.query,
              headers: exchange.request.headers,
            }}
          />
          <Json
            label="Response"
            value={{
              headers: exchange.response.headers,
              body: exchange.response.body,
            }}
          />
        </div>
      )}
    </div>
  );
}

function Json({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="mb-1 font-medium text-muted-foreground">{label}</div>
      <pre className="max-h-64 overflow-auto rounded bg-[var(--af-soft)] p-2 font-mono text-[11px] leading-snug">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function describeRequest(e: ExchangeEntry): string {
  if (e.pack === "email") return describeEmailRequest(e);
  const since = e.request.query.synchronized;
  if (e.operation === "issueFilter")
    return since ? `issues changed since ${since.slice(11)}` : "all issues";
  if (e.operation === "deletedIssues")
    return since
      ? `issues deleted since ${since.slice(11)}`
      : "all deleted issues";
  if (e.operation === "issueComments") return "comments on an issue";
  return e.request.path;
}

function describeResponse(e: ExchangeEntry): string {
  if (e.pack === "email") return describeEmailResponse(e);
  const body = e.response.body;
  if (e.response.status >= 400)
    return isRecord(body) ? String(body.message ?? "") : "";
  const items = itemsOf(e);
  if (e.operation === "issueFilter") {
    return items.length === 0
      ? "no changes"
      : `${items.length} issue${items.length === 1 ? "" : "s"}: ${items.map((i) => `#${i.id}`).join(", ")}`;
  }
  if (e.operation === "deletedIssues") {
    return items.length === 0
      ? "none deleted"
      : `deleted: ${items.map((i) => `#${i.id}`).join(", ")}`;
  }
  return `${items.length} comment${items.length === 1 ? "" : "s"}`;
}

const personOf = (world: World, email: string | null) =>
  world.people.find((p) => p.email === email);

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const ago = (ms: number) =>
  ms < 2000
    ? "just now"
    : ms < 90_000
      ? `${Math.round(ms / 1000)}s ago`
      : `${Math.round(ms / 60_000)} min ago`;

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}
